import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, Building2, MessageCircle, Mail, Copy, Check } from 'lucide-react';
import { bookingAPI } from '../services/api';
import { toast } from 'sonner';

// Payment Details Component
const PaymentDetailsCard = ({ amount }) => {
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-green-800 text-xl">Payment Options</CardTitle>
        <p className="text-sm text-green-700">Amount to pay: <span className="font-bold text-lg">{amount}</span></p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Mobile Money */}
        <div>
          <h4 className="font-semibold text-slate-800 flex items-center mb-3">
            <Phone className="w-5 h-5 mr-2 text-yellow-600" />
            Mobile Money
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* MTN */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="font-semibold text-yellow-800">MTN Mobile Money</p>
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Number:</span>
                  <button 
                    onClick={() => copyToClipboard('0555439160', 'mtn')}
                    className="flex items-center text-yellow-700 font-mono hover:text-yellow-900"
                  >
                    0555439160
                    {copiedField === 'mtn' ? <Check className="w-4 h-4 ml-1" /> : <Copy className="w-4 h-4 ml-1" />}
                  </button>
                </div>
                <p className="text-sm text-slate-600">Name: <span className="font-medium">Ralph Benjamin Teiko Quaye</span></p>
              </div>
            </div>
            
            {/* Telecel */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-semibold text-red-800">Telecel Cash</p>
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Number:</span>
                  <button 
                    onClick={() => copyToClipboard('0201358015', 'telecel')}
                    className="flex items-center text-red-700 font-mono hover:text-red-900"
                  >
                    0201358015
                    {copiedField === 'telecel' ? <Check className="w-4 h-4 ml-1" /> : <Copy className="w-4 h-4 ml-1" />}
                  </button>
                </div>
                <p className="text-sm text-slate-600">Name: <span className="font-medium">Ralph Benjamin Teiko Quaye</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Transfer */}
        <div>
          <h4 className="font-semibold text-slate-800 flex items-center mb-3">
            <Building2 className="w-5 h-5 mr-2 text-blue-600" />
            Bank Transfer
          </h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="font-semibold text-blue-800">Access Bank Ghana Limited</p>
            <div className="mt-2 space-y-1">
              <p className="text-sm text-slate-600">Account Name: <span className="font-medium">Streamline Aquatics Limited</span></p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Account Number:</span>
                <button 
                  onClick={() => copyToClipboard('0320223680231', 'bank')}
                  className="flex items-center text-blue-700 font-mono hover:text-blue-900"
                >
                  0320223680231
                  {copiedField === 'bank' ? <Check className="w-4 h-4 ml-1" /> : <Copy className="w-4 h-4 ml-1" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Send Proof */}
        <div className="bg-slate-100 rounded-lg p-4">
          <h4 className="font-semibold text-slate-800 mb-2">After Payment</h4>
          <p className="text-sm text-slate-600 mb-3">
            Send proof of payment (screenshot/receipt) via:
          </p>
          <div className="flex flex-wrap gap-3">
            <a 
              href="https://wa.me/233201358015?text=Hi%2C%20I%20just%20made%20a%20payment%20for%20swimming%20lessons.%20Here%20is%20my%20proof%20of%20payment."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp: +233 20 135 8015
            </a>
            <a 
              href="mailto:info@streamlineaquatics.co?subject=Payment%20Proof%20-%20Swimming%20Lesson%20Booking"
              className="inline-flex items-center px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors text-sm"
            >
              <Mail className="w-4 h-4 mr-2" />
              info@streamlineaquatics.co
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BookLesson = ({ user, onLogout }) => {
  const [lessonType, setLessonType] = useState('children');
  
  const [childrenFormData, setChildrenFormData] = useState({
    parent_name: '',
    parent_email: '',
    parent_gender: '',
    parent_contact: '',
    child_name: '',
    child_age: '',
    child_dob: '',
    area_of_residence: '',
    lesson_objective: 'fun',
    booking_type: 'per_session',
    preferred_start_date: '',
    preferred_schedule: '',
    liability_consent: false,
    media_consent: true,
  });

  const [adultsFormData, setAdultsFormData] = useState({
    name: '',
    gender: '',
    contact_number: '',
    email: '',
    age: '',
    dob: '',
    preferred_start_date: '',
    preferred_schedule: '',
    booking_type: 'per_session',
    lesson_reason: 'health',
  });

  const [loading, setLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);

  const getPricing = () => {
    if (lessonType === 'children') {
      return childrenFormData.booking_type === 'per_session' 
        ? { amount: 15000, display: 'GHS 150 per session' }
        : { amount: 150000, display: 'GHS 1,500 for 12 sessions' };
    } else {
      return adultsFormData.booking_type === 'per_session'
        ? { amount: 18000, display: 'GHS 180 per session' }
        : { amount: 180000, display: 'GHS 1,800 for 12 sessions' };
    }
  };

  const handleChildrenSubmit = async (e) => {
    e.preventDefault();
    
    if (!childrenFormData.liability_consent) {
      toast.error('Please accept the liability waiver to proceed');
      return;
    }

    setLoading(true);

    try {
      const pricing = getPricing();
      const bookingResponse = await bookingAPI.create({
        student_name: childrenFormData.child_name,
        student_email: childrenFormData.parent_email,
        student_phone: childrenFormData.parent_contact,
        student_age: parseInt(childrenFormData.child_age),
        date_of_birth: childrenFormData.child_dob || null,
        lesson_type: 'children',
        lesson_date: childrenFormData.preferred_start_date,
        lesson_time: childrenFormData.preferred_schedule,
        parent_name: childrenFormData.parent_name,
        parent_email: childrenFormData.parent_email,
        amount: pricing.amount,
      });

      setBookingSuccess({
        bookingId: bookingResponse.data.booking_id,
        clientId: bookingResponse.data.client_id,
        studentName: childrenFormData.child_name,
        amount: pricing.display
      });
      
      toast.success('Booking submitted successfully!');
      
      setChildrenFormData({
        parent_name: '',
        parent_email: '',
        parent_gender: '',
        parent_contact: '',
        child_name: '',
        child_age: '',
        child_dob: '',
        area_of_residence: '',
        lesson_objective: 'fun',
        booking_type: 'per_session',
        preferred_start_date: '',
        preferred_schedule: '',
        liability_consent: false,
        media_consent: true,
      });
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleAdultsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const pricing = getPricing();
      const bookingResponse = await bookingAPI.create({
        student_name: adultsFormData.name,
        student_email: adultsFormData.email,
        student_phone: adultsFormData.contact_number,
        student_age: parseInt(adultsFormData.age),
        date_of_birth: adultsFormData.dob || null,
        lesson_type: 'adults',
        lesson_date: adultsFormData.preferred_start_date,
        lesson_time: adultsFormData.preferred_schedule,
        amount: pricing.amount,
      });

      setBookingSuccess({
        bookingId: bookingResponse.data.booking_id,
        clientId: bookingResponse.data.client_id,
        studentName: adultsFormData.name,
        amount: pricing.display
      });
      
      toast.success('Booking submitted successfully!');
      
      setAdultsFormData({
        name: '',
        gender: '',
        contact_number: '',
        email: '',
        age: '',
        dob: '',
        preferred_start_date: '',
        preferred_schedule: '',
        booking_type: 'per_session',
        lesson_reason: 'health',
      });
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  // Show success page with payment instructions
  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="pt-32 pb-20">
          <div className="max-w-3xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-white shadow-xl border-2 border-green-200">
                <CardHeader className="text-center bg-green-50">
                  <div className="w-16 h-16 bg-green-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-green-800">Booking Submitted Successfully!</CardTitle>
                  <p className="text-green-700 mt-2">
                    Your booking has been received. Please make payment to confirm your spot.
                  </p>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-2">Booking Details</h4>
                    <p className="text-sm text-slate-600">Student: <span className="font-medium">{bookingSuccess.studentName}</span></p>
                    <p className="text-sm text-slate-600">Client ID: <span className="font-mono font-medium">{bookingSuccess.clientId}</span></p>
                    <p className="text-sm text-slate-600">Booking ID: <span className="font-mono text-xs">{bookingSuccess.bookingId}</span></p>
                  </div>

                  <PaymentDetailsCard amount={bookingSuccess.amount} />

                  <div className="text-center pt-4">
                    <Button 
                      onClick={() => setBookingSuccess(null)}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      Make Another Booking
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} onLogout={onLogout} />

      <div className="pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl font-extrabold text-slate-900 mb-4">Book a Swimming Lesson</h1>
            <p className="text-xl text-slate-600">
              Choose your program and secure your spot today!
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <Label className="text-lg font-semibold mb-4 block">Select Program</Label>
              <RadioGroup value={lessonType} onValueChange={setLessonType} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <RadioGroupItem value="children" id="children" className="peer sr-only" />
                  <Label
                    htmlFor="children"
                    className="flex flex-col items-center justify-between rounded-xl border-2 border-slate-200 bg-white p-6 hover:bg-slate-50 peer-data-[state=checked]:border-cyan-600 peer-data-[state=checked]:bg-cyan-50 cursor-pointer transition-all"
                    data-testid="children-lesson-option"
                  >
                    <span className="text-2xl mb-2">👶</span>
                    <span className="text-lg font-semibold">Learn to Swim - Children</span>
                    <span className="text-sm text-slate-600 mt-2">Ages 6 months - 16 years</span>
                    <span className="text-sm font-semibold text-cyan-700 mt-2">From GHS 150/session</span>
                  </Label>
                </div>
                <div className="relative">
                  <RadioGroupItem value="adults" id="adults" className="peer sr-only" />
                  <Label
                    htmlFor="adults"
                    className="flex flex-col items-center justify-between rounded-xl border-2 border-slate-200 bg-white p-6 hover:bg-slate-50 peer-data-[state=checked]:border-cyan-600 peer-data-[state=checked]:bg-cyan-50 cursor-pointer transition-all"
                    data-testid="adults-lesson-option"
                  >
                    <span className="text-2xl mb-2">🏊</span>
                    <span className="text-lg font-semibold">Learn to Swim - Adults</span>
                    <span className="text-sm text-slate-600 mt-2">Ages 17+ years</span>
                    <span className="text-sm font-semibold text-cyan-700 mt-2">From GHS 180/session</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </motion.div>

          {lessonType === 'children' && (
            <ChildrenBookingForm
              formData={childrenFormData}
              setFormData={setChildrenFormData}
              handleSubmit={handleChildrenSubmit}
              getPricing={getPricing}
              loading={loading}
            />
          )}

          {lessonType === 'adults' && (
            <AdultsBookingForm
              formData={adultsFormData}
              setFormData={setAdultsFormData}
              handleSubmit={handleAdultsSubmit}
              getPricing={getPricing}
              loading={loading}
            />
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

const ChildrenBookingForm = ({ formData, setFormData, handleSubmit, getPricing, loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2 }}
    className="bg-white rounded-3xl shadow-xl p-8 md:p-12"
  >
    <h2 className="text-3xl font-bold text-slate-900 mb-8">Children&apos;s Program Registration</h2>
    
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="border-b pb-6">
        <h3 className="text-xl font-semibold mb-4 text-cyan-700">Parent/Guardian Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="parent_name">Full Name *</Label>
            <Input
              id="parent_name"
              value={formData.parent_name}
              onChange={(e) => setFormData({...formData, parent_name: e.target.value})}
              required
              data-testid="parent-name-input"
            />
          </div>

          <div>
            <Label htmlFor="parent_gender">Gender *</Label>
            <Select
              value={formData.parent_gender}
              onValueChange={(value) => setFormData({...formData, parent_gender: value})}
            >
              <SelectTrigger data-testid="parent-gender-select">
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white">
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="parent_email">Email Address *</Label>
            <Input
              id="parent_email"
              type="email"
              value={formData.parent_email}
              onChange={(e) => setFormData({...formData, parent_email: e.target.value})}
              required
              data-testid="parent-email-input"
            />
          </div>

          <div>
            <Label htmlFor="parent_contact">Contact Number *</Label>
            <Input
              id="parent_contact"
              value={formData.parent_contact}
              onChange={(e) => setFormData({...formData, parent_contact: e.target.value})}
              placeholder="+233 XX XXX XXXX"
              required
              data-testid="parent-contact-input"
            />
          </div>
        </div>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-xl font-semibold mb-4 text-cyan-700">Child&apos;s Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="child_name">Child&apos;s Full Name *</Label>
            <Input
              id="child_name"
              value={formData.child_name}
              onChange={(e) => setFormData({...formData, child_name: e.target.value})}
              required
              data-testid="child-name-input"
            />
          </div>

          <div>
            <Label htmlFor="child_age">Age *</Label>
            <Input
              id="child_age"
              type="number"
              value={formData.child_age}
              onChange={(e) => setFormData({...formData, child_age: e.target.value})}
              required
              data-testid="child-age-input"
            />
          </div>

          <div>
            <Label htmlFor="child_dob">Date of Birth</Label>
            <Input
              id="child_dob"
              type="date"
              value={formData.child_dob}
              onChange={(e) => setFormData({...formData, child_dob: e.target.value})}
              data-testid="child-dob-input"
            />
          </div>

          <div>
            <Label htmlFor="area_of_residence">Area of Residence *</Label>
            <Input
              id="area_of_residence"
              value={formData.area_of_residence}
              onChange={(e) => setFormData({...formData, area_of_residence: e.target.value})}
              placeholder="e.g., Achimota, East Legon"
              required
              data-testid="residence-input"
            />
          </div>

          <div>
            <Label htmlFor="lesson_objective">Lesson Objective *</Label>
            <Select
              value={formData.lesson_objective}
              onValueChange={(value) => setFormData({...formData, lesson_objective: value})}
            >
              <SelectTrigger data-testid="objective-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white">
                <SelectItem value="fun">Fun/Recreation</SelectItem>
                <SelectItem value="competition">Competition</SelectItem>
                <SelectItem value="fitness">Health/Fitness</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-xl font-semibold mb-4 text-cyan-700">Schedule & Booking</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="booking_type">Booking Type *</Label>
            <Select
              value={formData.booking_type}
              onValueChange={(value) => setFormData({...formData, booking_type: value})}
            >
              <SelectTrigger data-testid="booking-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white">
                <SelectItem value="per_session">Per Session (GHS 150)</SelectItem>
                <SelectItem value="group">12 Sessions Package (GHS 1,500)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="preferred_start_date">Preferred Start Date *</Label>
            <Input
              id="preferred_start_date"
              type="date"
              value={formData.preferred_start_date}
              onChange={(e) => setFormData({...formData, preferred_start_date: e.target.value})}
              min={new Date().toISOString().split('T')[0]}
              required
              data-testid="start-date-input"
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="preferred_schedule">Preferred Schedule *</Label>
            <Select
              value={formData.preferred_schedule}
              onValueChange={(value) => setFormData({...formData, preferred_schedule: value})}
            >
              <SelectTrigger data-testid="schedule-select">
                <SelectValue placeholder="Select your preferred time" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white max-h-60 overflow-y-auto">
                <SelectItem value="weekday_7am">Weekdays - 7:00 AM - 8:00 AM</SelectItem>
                <SelectItem value="weekday_8am">Weekdays - 8:00 AM - 9:00 AM</SelectItem>
                <SelectItem value="weekday_9am">Weekdays - 9:00 AM - 10:00 AM</SelectItem>
                <SelectItem value="weekday_10am">Weekdays - 10:00 AM - 11:00 AM</SelectItem>
                <SelectItem value="weekday_11am">Weekdays - 11:00 AM - 12:00 PM</SelectItem>
                <SelectItem value="weekday_12pm">Weekdays - 12:00 PM - 1:00 PM</SelectItem>
                <SelectItem value="weekday_1pm">Weekdays - 1:00 PM - 2:00 PM</SelectItem>
                <SelectItem value="weekday_2pm">Weekdays - 2:00 PM - 3:00 PM</SelectItem>
                <SelectItem value="weekday_3pm">Weekdays - 3:00 PM - 4:00 PM</SelectItem>
                <SelectItem value="weekday_4pm">Weekdays - 4:00 PM - 5:00 PM</SelectItem>
                <SelectItem value="weekday_5pm">Weekdays - 5:00 PM - 6:00 PM</SelectItem>
                <SelectItem value="weekday_6pm">Weekdays - 6:00 PM - 7:00 PM</SelectItem>
                <SelectItem value="weekend_830am">Weekends - 8:30 AM - 9:30 AM</SelectItem>
                <SelectItem value="weekend_930am">Weekends - 9:30 AM - 10:30 AM</SelectItem>
                <SelectItem value="weekend_1030am">Weekends - 10:30 AM - 11:30 AM</SelectItem>
                <SelectItem value="weekend_1130am">Weekends - 11:30 AM - 12:30 PM</SelectItem>
                <SelectItem value="weekend_1230pm">Weekends - 12:30 PM - 1:30 PM</SelectItem>
                <SelectItem value="weekend_130pm">Weekends - 1:30 PM - 2:30 PM</SelectItem>
                <SelectItem value="weekend_230pm">Weekends - 2:30 PM - 3:30 PM</SelectItem>
                <SelectItem value="weekend_330pm">Weekends - 3:30 PM - 4:30 PM</SelectItem>
                <SelectItem value="weekend_430pm">Weekends - 4:30 PM - 5:30 PM</SelectItem>
                <SelectItem value="weekend_530pm">Weekends - 5:30 PM - 6:30 PM</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500 mt-1">Facility Hours: Mon-Fri 7am-7pm, Weekends 8:30am-7pm. Sessions are 45min-1hr. We recommend at least 2 sessions per week.</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-cyan-700">Consent & Agreements</h3>
        
        <div className="flex items-start space-x-3">
          <Checkbox
            id="liability_consent"
            checked={formData.liability_consent}
            onCheckedChange={(checked) => setFormData({...formData, liability_consent: checked})}
            required
            data-testid="liability-consent-checkbox"
          />
          <Label htmlFor="liability_consent" className="text-sm leading-relaxed cursor-pointer">
            I understand and accept that Streamline Aquatics Limited takes all reasonable precautions for safety, 
            but swimming carries inherent risks. I release Streamline Aquatics from liability for any injuries 
            occurring during normal swimming activities, except in cases of gross negligence. *
          </Label>
        </div>

        <div className="flex items-start space-x-3">
          <Checkbox
            id="media_consent"
            checked={formData.media_consent}
            onCheckedChange={(checked) => setFormData({...formData, media_consent: checked})}
            data-testid="media-consent-checkbox"
          />
          <Label htmlFor="media_consent" className="text-sm leading-relaxed cursor-pointer">
            I consent to Streamline Aquatics taking photographs/videos of my child for promotional purposes 
            (Check to agree, uncheck to opt out)
          </Label>
        </div>
      </div>

      <div className="bg-cyan-50 border-2 border-cyan-200 rounded-xl p-6">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-slate-900">Total Amount:</span>
          <span className="text-3xl font-bold text-cyan-700">
            {getPricing().display}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <Button
          type="submit"
          className="w-full btn-primary py-6 text-lg"
          disabled={loading || !formData.liability_consent}
          data-testid="submit-children-booking-btn"
        >
          {loading ? 'Processing...' : 'Submit Booking'}
        </Button>
      </div>
      
      <p className="text-sm text-slate-500 text-center">
        * After submitting, you will see payment details to complete your booking.
      </p>
    </form>
  </motion.div>
);

const AdultsBookingForm = ({ formData, setFormData, handleSubmit, getPricing, loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2 }}
    className="bg-white rounded-3xl shadow-xl p-8 md:p-12"
  >
    <h2 className="text-3xl font-bold text-slate-900 mb-8">Adult Program Registration</h2>
    
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="border-b pb-6">
        <h3 className="text-xl font-semibold mb-4 text-cyan-700">Personal Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="adult_name">Full Name *</Label>
            <Input
              id="adult_name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              data-testid="adult-name-input"
            />
          </div>

          <div>
            <Label htmlFor="adult_gender">Gender *</Label>
            <Select
              value={formData.gender}
              onValueChange={(value) => setFormData({...formData, gender: value})}
            >
              <SelectTrigger data-testid="adult-gender-select">
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white">
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="adult_email">Email Address *</Label>
            <Input
              id="adult_email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              data-testid="adult-email-input"
            />
          </div>

          <div>
            <Label htmlFor="adult_contact">Contact Number *</Label>
            <Input
              id="adult_contact"
              value={formData.contact_number}
              onChange={(e) => setFormData({...formData, contact_number: e.target.value})}
              placeholder="+233 XX XXX XXXX"
              required
              data-testid="adult-contact-input"
            />
          </div>

          <div>
            <Label htmlFor="adult_age">Age *</Label>
            <Input
              id="adult_age"
              type="number"
              value={formData.age}
              onChange={(e) => setFormData({...formData, age: e.target.value})}
              required
              data-testid="adult-age-input"
            />
          </div>

          <div>
            <Label htmlFor="adult_dob">Date of Birth</Label>
            <Input
              id="adult_dob"
              type="date"
              value={formData.dob}
              onChange={(e) => setFormData({...formData, dob: e.target.value})}
              data-testid="adult-dob-input"
            />
          </div>

          <div>
            <Label htmlFor="lesson_reason">Reason for Lessons *</Label>
            <Select
              value={formData.lesson_reason}
              onValueChange={(value) => setFormData({...formData, lesson_reason: value})}
            >
              <SelectTrigger data-testid="lesson-reason-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white">
                <SelectItem value="health">Health/Fitness</SelectItem>
                <SelectItem value="weight_loss">Weight Loss</SelectItem>
                <SelectItem value="fun">Fun/Recreation</SelectItem>
                <SelectItem value="competition">Competition</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-xl font-semibold mb-4 text-cyan-700">Schedule & Booking</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="booking_type">Booking Type *</Label>
            <Select
              value={formData.booking_type}
              onValueChange={(value) => setFormData({...formData, booking_type: value})}
            >
              <SelectTrigger data-testid="booking-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white">
                <SelectItem value="per_session">Per Session (GHS 180)</SelectItem>
                <SelectItem value="package">12 Sessions Package (GHS 1,800)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500 mt-1">For 1-on-1 private classes, please contact us directly.</p>
          </div>

          <div>
            <Label htmlFor="adult_start_date">Preferred Start Date *</Label>
            <Input
              id="adult_start_date"
              type="date"
              value={formData.preferred_start_date}
              onChange={(e) => setFormData({...formData, preferred_start_date: e.target.value})}
              min={new Date().toISOString().split('T')[0]}
              required
              data-testid="adult-start-date-input"
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="adult_schedule">Preferred Schedule *</Label>
            <Select
              value={formData.preferred_schedule}
              onValueChange={(value) => setFormData({...formData, preferred_schedule: value})}
            >
              <SelectTrigger data-testid="adult-schedule-select">
                <SelectValue placeholder="Select your preferred time" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white max-h-60 overflow-y-auto">
                <SelectItem value="weekday_7am">Weekdays - 7:00 AM - 8:00 AM</SelectItem>
                <SelectItem value="weekday_8am">Weekdays - 8:00 AM - 9:00 AM</SelectItem>
                <SelectItem value="weekday_9am">Weekdays - 9:00 AM - 10:00 AM</SelectItem>
                <SelectItem value="weekday_10am">Weekdays - 10:00 AM - 11:00 AM</SelectItem>
                <SelectItem value="weekday_11am">Weekdays - 11:00 AM - 12:00 PM</SelectItem>
                <SelectItem value="weekday_12pm">Weekdays - 12:00 PM - 1:00 PM</SelectItem>
                <SelectItem value="weekday_1pm">Weekdays - 1:00 PM - 2:00 PM</SelectItem>
                <SelectItem value="weekday_2pm">Weekdays - 2:00 PM - 3:00 PM</SelectItem>
                <SelectItem value="weekday_3pm">Weekdays - 3:00 PM - 4:00 PM</SelectItem>
                <SelectItem value="weekday_4pm">Weekdays - 4:00 PM - 5:00 PM</SelectItem>
                <SelectItem value="weekday_5pm">Weekdays - 5:00 PM - 6:00 PM</SelectItem>
                <SelectItem value="weekday_6pm">Weekdays - 6:00 PM - 7:00 PM</SelectItem>
                <SelectItem value="weekend_830am">Weekends - 8:30 AM - 9:30 AM</SelectItem>
                <SelectItem value="weekend_930am">Weekends - 9:30 AM - 10:30 AM</SelectItem>
                <SelectItem value="weekend_1030am">Weekends - 10:30 AM - 11:30 AM</SelectItem>
                <SelectItem value="weekend_1130am">Weekends - 11:30 AM - 12:30 PM</SelectItem>
                <SelectItem value="weekend_1230pm">Weekends - 12:30 PM - 1:30 PM</SelectItem>
                <SelectItem value="weekend_130pm">Weekends - 1:30 PM - 2:30 PM</SelectItem>
                <SelectItem value="weekend_230pm">Weekends - 2:30 PM - 3:30 PM</SelectItem>
                <SelectItem value="weekend_330pm">Weekends - 3:30 PM - 4:30 PM</SelectItem>
                <SelectItem value="weekend_430pm">Weekends - 4:30 PM - 5:30 PM</SelectItem>
                <SelectItem value="weekend_530pm">Weekends - 5:30 PM - 6:30 PM</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500 mt-1">Facility Hours: Mon-Fri 7am-7pm, Weekends 8:30am-7pm. Session duration: 45min-1hr</p>
          </div>
        </div>
      </div>

      <div className="bg-cyan-50 border-2 border-cyan-200 rounded-xl p-6">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-slate-900">Total Amount:</span>
          <span className="text-3xl font-bold text-cyan-700">
            {getPricing().display}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <Button
          type="submit"
          className="w-full btn-primary py-6 text-lg"
          disabled={loading}
          data-testid="submit-adult-booking-btn"
        >
          {loading ? 'Processing...' : 'Submit Booking'}
        </Button>
      </div>
      
      <p className="text-sm text-slate-500 text-center">
        * After submitting, you will see payment details to complete your booking.
      </p>
    </form>
  </motion.div>
);

export default BookLesson;
